import { translateTicketDocumentType } from '@common/intl/ticket-document-type'
import type { TicketEventFragment, UserFragment } from '@gql'
import { Permission, TicketEventType } from '@gql'
import { Tooltip } from '@mui/material'
import { hasPermissions } from '@sections/auth/access'
import { userDetailUrl } from '@sections/users/urls'
import type { ReactElement, ReactNode } from 'react'
import type { IntlShape } from 'react-intl'
import type { MessageValue } from 'react-intl'
import { Link } from 'react-router'

export function translateTicketEvent(
  intl: IntlShape,
  event: TicketEventFragment,
  user: UserFragment | null | undefined,
): string | Exclude<ReactNode, undefined> {
  switch (event.type) {
    case TicketEventType.CREATED:
      return intl.formatMessage({
        defaultMessage: 'Ticket was created',
        description: 'ticket history message',
      })
    case TicketEventType.ASSIGNED:
      return intl.formatMessage<{ readonly userName: MessageValue }, ReactElement>(
        {
          defaultMessage: 'Ticket was assigned to user {userName}',
          description: 'ticket history message',
        },
        {
          userName: event.user ? (
            hasPermissions([Permission.MANAGE_USERS], user) ? (
              <Link key={event.user.id} to={userDetailUrl(event.user.id)}>
                <Tooltip disableInteractive title={event.user.displayName}>
                  <b>{event.user.displayName}</b>
                </Tooltip>
              </Link>
            ) : (
              <b>{event.user.displayName}</b>
            )
          ) : (
            'unknown'
          ),
        },
      )
    case TicketEventType.CLOSED:
      return intl.formatMessage({
        defaultMessage: 'Ticket was closed',
        description: 'ticket history message',
      })
    case TicketEventType.NOTE_ADDED:
      return intl.formatMessage({
        defaultMessage: 'Note added',
        description: 'ticket history message',
      })
    case TicketEventType.ACTION_ADDED:
      return intl.formatMessage<{ readonly result: MessageValue; readonly type: MessageValue }>(
        {
          defaultMessage: '{type} action with result {result} created',
          description: 'ticket history message',
        },
        {
          result: event.actionResult?.name,
          type: event.actionType?.name,
        },
      )
    case TicketEventType.ACTION_EDITED:
      return intl.formatMessage<{ readonly result: MessageValue; readonly type: MessageValue }>(
        {
          defaultMessage: '{type} action with result {result} edited',
          description: 'ticket history message',
        },
        {
          result: event.actionResult?.name,
          type: event.actionType?.name,
        },
      )
    case TicketEventType.ACTION_DELETED:
      return intl.formatMessage<{ readonly result: MessageValue; readonly type: MessageValue }>(
        {
          defaultMessage: '{type} action with result {result} has been deleted',
          description: 'ticket history message',
        },
        {
          result: event.actionResult?.name,
          type: event.actionType?.name,
        },
      )
    case TicketEventType.DOCUMENT_UPLOADED:
      return intl.formatMessage<{ readonly type: MessageValue }>(
        {
          defaultMessage: 'Document {type} was uploaded',
          description: 'ticket history message',
        },
        {
          type: translateTicketDocumentType(
            intl,
            event.parameters.type,
            event.parameters.otherType ?? null,
          ),
        },
      )
    case TicketEventType.DOCUMENT_EDITED:
      return intl.formatMessage<{ readonly type: MessageValue }>(
        {
          defaultMessage: 'Document {type} was edited',
          description: 'ticket history message',
        },
        {
          type: translateTicketDocumentType(
            intl,
            event.parameters.type,
            event.parameters.otherType ?? null,
          ),
        },
      )
    case TicketEventType.DOCUMENT_DELETED:
      return intl.formatMessage<{ readonly type: MessageValue }>(
        {
          defaultMessage: 'Document {type} was deleted',
          description: 'ticket history message',
        },
        {
          type: translateTicketDocumentType(
            intl,
            event.parameters.type,
            event.parameters.otherType ?? null,
          ),
        },
      )
    case TicketEventType.TAG_ATTACHED:
      if (event.parameters.tagName) {
        return intl.formatMessage<{ readonly tagName: MessageValue }>(
          {
            defaultMessage: 'Tag {tagName} attached.',
            description: 'ticket history message',
          },
          {
            tagName: event.parameters.tagName,
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Tag attached.',
          description: 'ticket history message',
        })
      }

    case TicketEventType.TAG_DETACHED:
      if (event.parameters.tagName) {
        return intl.formatMessage<{ readonly tagName: MessageValue }>(
          {
            defaultMessage: 'Tag {tagName} detached.',
            description: 'ticket history message',
          },
          {
            tagName: event.parameters.tagName,
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Tag detached.',
          description: 'ticket history message',
        })
      }
  }
}
